/* eslint-disable react/prop-types */
/*
 * Copyright (c) 2025. Tooling Team
 */
import { PATHS } from "../utils/constants";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import Layout from "../components/layout/Layout";
import { useEffect, useMemo } from "react";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { Tabs } from "@uhg-abyss/web/ui/Tabs";
import { useState } from "react";
import { Badge } from "@uhg-abyss/web/ui/Badge";
import { Button } from "@uhg-abyss/web/ui/Button";
import useMedDomain from "../hooks/useMedDomain";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import moment from "moment";

export function MedDomainDetails() {
  const { build_id } = useParams();
  const location = useLocation();
  const formattedEndTime = location.state?.end_time
    ? moment(location.state?.end_time).format("YYYY.MM.DD HH:mm")
    : "";
  const buildGroupId = location.state?.groupid;

  const {
    state: { medDomainBuildSummaryData, medDomainBuildDetailData },
    fetchMedDomainBuildDetail,
    isLoadingDetail,
  } = useMedDomain(build_id);
  const [lastRequestedMapUsed, setLastRequestedMapUsed] = useState("addon");
  const [detailDataByType, setDetailDataByType] = useState({
    ndc: [],
    rxnorm: [],
    addon: [],
    fixed: [],
    missed: [],
    null: [],
  });
  const columns = useMemo(
    () => [
      {
        header: "Group ID",
        accessorKey: "groupId",
      },
      {
        header: "Data Source",
        accessorKey: "dataSource",
      },
      {
        header: "Client DS ID",
        accessorKey: "clientDsId",
      },
      {
        header: "RX Tab",
        accessorKey: "rxTab",
      },
      {
        header: "Cnt Lmc",
        accessorKey: "cntLmc",
      },
      {
        header: "Num RXs",
        accessorKey: "numRxs",
      },
      {
        header: "NDC",
        accessorKey: "ndc",
      },
      {
        header: "RX Norm",
        accessorKey: "rxNorm",
      },
      {
        header: "Addon",
        accessorKey: "addon",
      },
      {
        header: "Unmap",
        accessorKey: "unmap",
      },
      {
        header: "Fixed",
        accessorKey: "fixed",
      },
      {
        header: "Missed",
        accessorKey: "missed",
      },
      {
        header: "Total Mapped Pct",
        accessorKey: "totalMapPct",
      },
      {
        header: "Null Map",
        accessorKey: "nullMap",
      },
    ],
    []
  );

  const detailColumns = useMemo(
    () => [
      {
        header: "Client DS ID",
        accessorKey: "clientDsId",
      },
      {
        header: "Data Source",
        accessorKey: "dataSource",
      },
      {
        header: "DCC",
        accessorKey: "dcc",
      },
      {
        header: "Group ID",
        accessorKey: "groupId",
      },
      {
        header: "HGMK_DCC",
        accessorKey: "hgmkDcc",
      },
      {
        header: "HMK_DCC",
        accessorKey: "hmkDcc",
      },
      {
        header: "Local Description",
        accessorKey: "localDesc",
      },
      {
        header: "Num RXs",
        accessorKey: "numRxs",
      },
      {
        header: "RX Tab",
        accessorKey: "rxTab",
      },
      {
        header: "Predicted DCC",
        accessorKey: "predictedDcc",
      },
      {
        header: "Probability",
        accessorKey: "probability",
      },
      {
        header: "ML Needs Review",
        accessorKey: "mlNeedsReview",
      },
    ],
    []
  );
  const data = medDomainBuildSummaryData;

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
  });

  const [ndcpagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const ndcDetailTableProps = useDataTable({
    initialData: detailDataByType.ndc,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: ndcpagination,
      },
      onPaginationChange: setPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });

  const [rxnormPagination, setRxnormPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const rxnormDetailTableProps = useDataTable({
    initialData: detailDataByType.rxnorm,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: rxnormPagination,
      },
      onPaginationChange: setRxnormPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });

  const [addonPagination, setAddonPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const addonDetailTableProps = useDataTable({
    initialData: detailDataByType.addon,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: addonPagination,
      },
      onPaginationChange: setAddonPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });

  const [fixedPagination, setFixedPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const fixedDetailTableProps = useDataTable({
    initialData: detailDataByType.fixed,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: fixedPagination,
      },
      onPaginationChange: setFixedPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });
  const [missedPagination, setMissedPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const missedDetailTableProps = useDataTable({
    initialData: detailDataByType.missed,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: missedPagination,
      },
      onPaginationChange: setMissedPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });
  const [nullPagination, setNullPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const nullDetailTableProps = useDataTable({
    initialData: detailDataByType.null,
    initialColumns: detailColumns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination: nullPagination,
      },
      onPaginationChange: setNullPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [25, 50, 75, 100, 200],
    },
  });

  useEffect(() => {
    try {
      if (
        Array.isArray(medDomainBuildSummaryData) &&
        typeof dataTableProps.setData === "function"
      ) {
        dataTableProps.setData(medDomainBuildSummaryData, true);
      }
    } catch (e) {
      console.error("Error in setData useEffect:", e);
    }
  }, [medDomainBuildSummaryData, dataTableProps.setData]);

  useEffect(() => {
    if (!lastRequestedMapUsed) {
      return;
    }

    setDetailDataByType((prev) => ({
      ...prev,
      [lastRequestedMapUsed]: Array.isArray(medDomainBuildDetailData)
        ? medDomainBuildDetailData
        : [],
    }));
  }, [lastRequestedMapUsed, medDomainBuildDetailData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.ndc)) {
      ndcDetailTableProps.setData(detailDataByType.ndc, true);
    }
  }, [detailDataByType.ndc, ndcDetailTableProps.setData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.rxnorm)) {
      rxnormDetailTableProps.setData(detailDataByType.rxnorm, true);
    }
  }, [detailDataByType.rxnorm, rxnormDetailTableProps.setData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.addon)) {
      addonDetailTableProps.setData(detailDataByType.addon, true);
    }
  }, [detailDataByType.addon, addonDetailTableProps.setData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.fixed)) {
      fixedDetailTableProps.setData(detailDataByType.fixed, true);
    }
  }, [detailDataByType.fixed, fixedDetailTableProps.setData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.missed)) {
      missedDetailTableProps.setData(detailDataByType.missed, true);
    }
  }, [detailDataByType.missed, missedDetailTableProps.setData]);

  useEffect(() => {
    if (Array.isArray(detailDataByType.null)) {
      nullDetailTableProps.setData(detailDataByType.null, true);
    }
  }, [detailDataByType.null, nullDetailTableProps.setData]);

  const handleDetailRequest = (mapUsed) => {
    setLastRequestedMapUsed(mapUsed);
    fetchMedDomainBuildDetail(mapUsed);
  };

  return (
    <Layout>
      <RouteDisplay pageRouteDisplay={[PATHS.medDomainDetails]} />
      <div className="row mb-4">
        <h3 className="mb-4">
          {buildGroupId + " - " + formattedEndTime + " - Build " + build_id}
        </h3>
        <DataTable title="Summary" tableState={dataTableProps}>
          <DataTable.Table />
        </DataTable>
      </div>
      <div className="row mb-4">
        <h3>Detail</h3>
      </div>
      <Tabs title="Tabs Sandbox">
        <Tabs.Tab label="NDC">
          <div className="row mb-4">
            <div className="d-flex">
              Default sort:
              <Badge rounded outline variant="neutral" className="ms-2">
                Client (ASC)
              </Badge>
            </div>
          </div>
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("ndc")}
                disabled={isLoadingDetail}
              >
                Load NDC Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable tableState={ndcDetailTableProps}>
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label="RxNorm">
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("rxnorm")}
                disabled={isLoadingDetail}
              >
                Load RxNorm Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable
              hideTitleHeader="true"
              tableState={rxnormDetailTableProps}
            >
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label="Addon">
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("addon")}
                disabled={isLoadingDetail}
              >
                Load Addon Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable tableState={addonDetailTableProps}>
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label="Fixed">
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("fixed")}
                disabled={isLoadingDetail}
              >
                Load Fixed Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable tableState={fixedDetailTableProps}>
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label="Missed">
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("missed")}
                disabled={isLoadingDetail}
              >
                Load Missed Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable tableState={missedDetailTableProps}>
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label="Null">
          <div className="row mb-3">
            <div className="d-flex justify-content-start">
              <Button
                onClick={() => handleDetailRequest("null")}
                disabled={isLoadingDetail}
              >
                Load Null Detail
              </Button>
            </div>
          </div>
          <div className="row">
            <DataTable tableState={nullDetailTableProps}>
              <DataTable.Pagination
                variant="extended"
                showPageSizeDropdown={true}
              />
              <DataTable.Table />
            </DataTable>
          </div>
        </Tabs.Tab>
      </Tabs>
    </Layout>
  );
}
